
import { Action } from 'redux';

import { SubscriptionType } from 'common/constants/subscription';
import { Role } from 'common/interfaces/account/role';
import { ActionWith } from 'common/interfaces/action-with';
import { EstimateSubscriptionError } from 'unit-subscription/interfaces/estimate-subscription-error';
import { BillingCountryModel } from '../interfaces/billing-country-model';
import { CompanyBillingInfoModel } from '../interfaces/company-billing-info-model';
import { CreateBillingCardForm } from '../interfaces/create-billing-card-form';
import { CreateSubscriptionForm } from '../interfaces/create-subscription-form';
import { EstimateSubscriptionForm } from '../interfaces/estimate-subscription-form';
import { EstimateSubscriptionModel } from '../interfaces/estimation-subscription-model';
import { PaymentSourcesModel } from '../interfaces/payment-sources-model';
import { SubscriptionPlanListingModel } from '../interfaces/subscription-plan-listing-model';
import { UpdateSubscriptionForm } from '../interfaces/update-subscription-form';
import {
  CreateSubscriptionEstimationPayload,
  FormWithSubscriptionId,
  SubscriptionAndCompanyIdPayload,
} from './payloads';
import { PaymentMethodActionTypes, SubscriptionActionTypes } from './types';

function setSubscriptionTypeAndCompanyId(
  subscriptionType: SubscriptionType,
  companyId: number,
): ActionWith<SubscriptionAndCompanyIdPayload> {
  return {
    type: SubscriptionActionTypes.SET_SUBSCRIPTION_TYPE_AND_COMPANY_ID,
    payload: { subscriptionType, companyId },
  };
}

function loadSubscriptionRoles(): Action {
  return {
    type: SubscriptionActionTypes.LOAD_SUBSCRIPTION_ROLES,
  };
}

function loadSubscriptionRolesSucceeded(roles: Role[]): ActionWith<Role[]> {
  return {
    type: SubscriptionActionTypes.LOAD_SUBSCRIPTION_ROLES_SUCCEEDED,
    payload: roles,
  };
}

// function updateSubscriptionPermissionRoles(
//   rolesPermissions: UpdateRolePermissions[],
// ): ActionWith<UpdateRolePermissions[]> {
//   return {
//     type: SubscriptionActionTypes.UPDATE_SUBSCRIPTION_PERMISSION_ROLES_REQUEST,
//     payload: rolesPermissions,
//   };
// }

// function updateSubscriptionPermissionRolesSucceeded(
//   rolesPermissions: Record<number, UserPermission[]>,
// ): ActionWith<Record<number, UserPermission[]>> {
//   return {
//     type: SubscriptionActionTypes.UPDATE_SUBSCRIPTION_PERMISSION_ROLES_SUCCEEDED,
//     payload: rolesPermissions,
//   };
// }

// function updateSubscriptionRoles(): Action {
//   return {
//     type: SubscriptionActionTypes.UPDATE_SUBSCRIPTION_ROLES_REQUEST,
//   };
// }

// function updateSubscriptionRolesSucceeded(): Action {
//   return {
//     type: SubscriptionActionTypes.UPDATE_SUBSCRIPTION_ROLES_SUCCEEDED,
//   };
// }


function getSubscriptionPlansRequest(subscriptionType: SubscriptionType): ActionWith<SubscriptionType> {
  return {
    type: SubscriptionActionTypes.GET_SUBSCRIPTION_PLANS_REQUEST,
    payload: subscriptionType,
  };
}

function getSubscriptionPlansSucceeded(plans: SubscriptionPlanListingModel): ActionWith<SubscriptionPlanListingModel> {
  return {
    type: SubscriptionActionTypes.GET_SUBSCRIPTION_PLANS_SUCCEEDED,
    payload: plans,
  };
}

function getSubscriptionPlansFailed(): Action {
  return {
    type: SubscriptionActionTypes.GET_SUBSCRIPTION_PLANS_FAILED,
  };
}

function createSubscription(form: CreateSubscriptionForm): ActionWith<CreateSubscriptionForm> {
  return {
    type: SubscriptionActionTypes.CREATE_SUBSCRIPTION,
    payload: form,
  };
}

function updateSubscription(
  subscriptionId: string,
  form: UpdateSubscriptionForm,
): ActionWith<FormWithSubscriptionId<UpdateSubscriptionForm>> {
  return {
    type: SubscriptionActionTypes.UPDATE_SUBSCRIPTION,
    payload: { form, subscriptionId },
  };
}

function cancelSubscription(id: string): ActionWith<string> {
  return {
    type: SubscriptionActionTypes.CANCEL_SUBSCRIPTION,
    payload: id,
  };
}

function renewSubscription(
  subscriptionId: string,
  form: UpdateSubscriptionForm,
): ActionWith<FormWithSubscriptionId<UpdateSubscriptionForm>> {
  return {
    type: SubscriptionActionTypes.RENEW_SUBSCRIPTION,
    payload: { form, subscriptionId },
  };
}

function getUpdateSubscriptionEstimation(
  subscriptionId: string,
  form: EstimateSubscriptionForm,
): ActionWith<FormWithSubscriptionId<EstimateSubscriptionForm>> {
  return {
    type: SubscriptionActionTypes.GET_UPDATE_SUBSCRIPTION_ESTIMATION,
    payload: { form, subscriptionId },
  };
}

function getCreateSubscriptionEstimation(
  subscriptionType: SubscriptionType,
  form: EstimateSubscriptionForm,
): ActionWith<CreateSubscriptionEstimationPayload> {
  return {
    type: SubscriptionActionTypes.GET_CREATE_SUBSCRIPTION_ESTIMATION,
    payload: { form, subscriptionType },
  };
}


function setSubscriptionEstimation(model: EstimateSubscriptionModel): ActionWith<EstimateSubscriptionModel> {
  return {
    type: SubscriptionActionTypes.SET_SUBSCRIPTION_ESTIMATION,
    payload: model,
  };
}

function setSubscriptionEstimationError(error: EstimateSubscriptionError): ActionWith<EstimateSubscriptionError> {
  return {
    type: SubscriptionActionTypes.SET_SUBSCRIPTION_ESTIMATION_ERROR,
    payload: error,
  };
}


function setSubscriptionPaymentMethod(
  subscriptionId: string,
  paymentMethodId: string,
): ActionWith<FormWithSubscriptionId<string>> {
  return {
    type: SubscriptionActionTypes.SET_SUBSCRIPTION_PAYMENT_METHOD_REQUEST,
    payload: {
      subscriptionId,
      form: paymentMethodId,
    },
  };
}

function fetchOwnCompanyBillingInfo(subscriptionType: SubscriptionType): ActionWith<SubscriptionType> {
  return {
    type: SubscriptionActionTypes.FETCH_OWN_COMPANY_BILLING_INFO_REQUEST,
    payload: subscriptionType,
  };
}

function fetchOwnCompanyBillingInfoSucceeded(model: CompanyBillingInfoModel): ActionWith<CompanyBillingInfoModel> {
  return {
    type: SubscriptionActionTypes.FETCH_OWN_COMPANY_BILLING_INFO_SUCCEEDED,
    payload: model,
  };
}

function fetchOwnCompanyBillingInfoFailed(): Action {
  return {
    type: SubscriptionActionTypes.FETCH_OWN_COMPANY_BILLING_INFO_FAILED,
  };
}

function dropSubscriptionPlansState(): Action {
  return {
    type: SubscriptionActionTypes.DROP_SUBSCRIPTION_PLANS_STATE,
  };
}

function dropOwnCompanyBillingInfoState(): Action {
  return {
    type: SubscriptionActionTypes.DROP_OWN_COMPANY_BILLING_INFO_STATE,
  };
}

export const SubscriptionActions = {
  setSubscriptionTypeAndCompanyId,
  loadSubscriptionRoles,
  loadSubscriptionRolesSucceeded,
  // updateSubscriptionPermissionRoles,
  // updateSubscriptionPermissionRolesSucceeded,
  // updateSubscriptionRoles,
  // updateSubscriptionRolesSucceeded,

  getSubscriptionPlansRequest,
  getSubscriptionPlansSucceeded,
  getSubscriptionPlansFailed,

  createSubscription,
  updateSubscription,
  cancelSubscription,
  renewSubscription,

  getUpdateSubscriptionEstimation,
  getCreateSubscriptionEstimation,
  setSubscriptionEstimation,
  setSubscriptionEstimationError,

  setSubscriptionPaymentMethod,

  fetchOwnCompanyBillingInfo,
  fetchOwnCompanyBillingInfoSucceeded,
  fetchOwnCompanyBillingInfoFailed,

  dropSubscriptionPlansState,
  dropOwnCompanyBillingInfoState,
};

function setPrimaryCard(id: string): ActionWith<string> {
  return {
    type: PaymentMethodActionTypes.SET_PRIMARY_CARD,
    payload: id,
  };
}

function setPrimaryCardSucceeded(model: PaymentSourcesModel): ActionWith<PaymentSourcesModel> {
  return {
    type: PaymentMethodActionTypes.SET_PRIMARY_CARD_SUCCEEDED,
    payload: model,
  };
}

function setPrimaryCardFailed(): Action {
  return {
    type: PaymentMethodActionTypes.SET_PRIMARY_CARD_FAILED,
  };
}


function setBackupCard(id: string): ActionWith<string> {
  return {
    type: PaymentMethodActionTypes.SET_BACKUP_CARD,
    payload: id,
  };
}

function setBackupCardSucceeded(model: PaymentSourcesModel): ActionWith<PaymentSourcesModel> {
  return {
    type: PaymentMethodActionTypes.SET_BACKUP_CARD_SUCCEEDED,
    payload: model,
  };
}

function setBackupCardFailed(): Action {
  return {
    type: PaymentMethodActionTypes.SET_BACKUP_CARD_FAILED,
  };
}


function getPaymentMethods(): Action {
  return {
    type: PaymentMethodActionTypes.GET_PAYMENT_METHODS,
  };
}

function getPaymentMethodsSucceeded(model: PaymentSourcesModel): ActionWith<PaymentSourcesModel> {
  return {
    type: PaymentMethodActionTypes.GET_PAYMENT_METHODS_SUCCEEDED,
    payload: model,
  };
}

function getPaymentMethodsFailed(): Action {
  return {
    type: PaymentMethodActionTypes.GET_PAYMENT_METHODS_FAILED,
  };
}

function setAvailableBillingCountries(billingCountries: BillingCountryModel[]): ActionWith<BillingCountryModel[]> {
  return {
    type: PaymentMethodActionTypes.SET_AVAILABLE_BILLING_COUNTRIES,
    payload: billingCountries,
  };
}

function removeCard(id: string): ActionWith<string> {
  return {
    type: PaymentMethodActionTypes.REMOVE_CARD,
    payload: id,
  };
}

function removeCardSucceeded(model: PaymentSourcesModel): ActionWith<PaymentSourcesModel> {
  return {
    type: PaymentMethodActionTypes.REMOVE_CARD_SUCCEEDED,
    payload: model,
  };
}

function removeCardFailed(): Action {
  return {
    type: PaymentMethodActionTypes.REMOVE_CARD_FAILED,
  };
}


function createCard(form: CreateBillingCardForm): ActionWith<CreateBillingCardForm> {
  return {
    type: PaymentMethodActionTypes.CREATE_CARD,
    payload: form,
  };
}

function createCardSucceeded(model: PaymentSourcesModel): ActionWith<PaymentSourcesModel> {
  return {
    type: PaymentMethodActionTypes.CREATE_CARD_SUCCEEDED,
    payload: model,
  };
}

function createCardFailed(): Action {
  return {
    type: PaymentMethodActionTypes.CREATE_CARD_FAILED,
  };
}

function dropPaymentState(): Action {
  return {
    type: PaymentMethodActionTypes.DROP_PAYMENT_STATE,
  };
}

export const PaymentMethodActions = {
  setPrimaryCard,
  setPrimaryCardSucceeded,
  setPrimaryCardFailed,

  setBackupCard,
  setBackupCardSucceeded,
  setBackupCardFailed,

  getPaymentMethods,
  getPaymentMethodsSucceeded,
  getPaymentMethodsFailed,

  setAvailableBillingCountries,

  removeCard,
  removeCardSucceeded,
  removeCardFailed,

  createCard,
  createCardSucceeded,
  createCardFailed,

  dropPaymentState,
};

